'use client';
import classNames from 'classnames';
import { useEffect, useRef, useState } from 'react';
import './index.scss';

export interface EcoVideoProps {
    /**
     * 所有分辨率展示的视频链接
     */
    pc?: string;
    /**
     * 视频链接-分辨率小于等于1024px 或768px
     */
    h5?: string;
    /**
     * 视频链接-分辨率小于等于1024px
     */
    pad?: string;
    /**
     * 视频贴图-所有分辨率
     */
    poster?: string;
    /**
     * 视频贴图-分辨率[1024, 0) 或 (768, 0)
     */
    posterH5?: string;
    /**
     * 视频贴图-分辨率[1024, 768]
     */
    posterPad?: string;
    /**
     * 是否自动播放
     */
    autoPlay?: boolean;
    /**
     * 是否循环播放
     */
    loop?: boolean;
    /**
     * 样式名
     */
    className?: string;
    /**
     * 不使用到可视再展示
     */
    isDirectUseSrc?: boolean;
}

/**
 * 用于播放视频
 *
 * 视频右上角会有个播放按钮,在不播放时展示
 */
export default function EcoVideo({ className = '', autoPlay, loop = false, poster, pc, h5, isDirectUseSrc = false }: EcoVideoProps) {
    const prefixCls = 'eco-video';
    const compCls = classNames(prefixCls, {
        [`${className}`]: className,
    });
    const refVideoBox = useRef<any>(null);
    const videoRef1 = useRef(null);
    const videoRef2 = useRef(null);
    const [showPlayIcon, setShowPlayIcon] = useState(!autoPlay);
    const [isPlay, setIsPlay] = useState(false);
    const pcSrc = pc;
    const twoSrc = pcSrc && h5;
    const [pcA, setPcA] = useState(isDirectUseSrc ? pc : '');
    const [h5A, setH5A] = useState(isDirectUseSrc ? h5 : '');
    // 获取选中的元素
    const getShowVideo = () => {
        const videoA: any = videoRef1?.current;
        const videoB: any = videoRef2?.current;
        const blockA = videoA && window.getComputedStyle(videoA).display;
        const blockB = videoB && window.getComputedStyle(videoB).display;
        if (blockA === 'block') {
            return videoRef1;
        }
        if (blockB === 'block') {
            return videoRef2;
        }

        return null;
    };
    // 点击播放
    const playHandle = () => {
        setIsPlay(true);
        const videoA: any = getShowVideo()?.current;

        if (videoA) {
            videoA.play();
            setIsPlay(true);
            setShowPlayIcon(false);
        }
    };
    useEffect(() => {
        const videoA: any = getShowVideo()?.current;

        if (videoA) {
            videoA.addEventListener('ended', () => {
                setIsPlay(false);
                setShowPlayIcon(true);
            });
        }
    }, []);

    useEffect(() => {
        const options = {
            rootMargin: '100px', //偏移量,-100px表示从底部伸出100px了
            threshold: 0, //0 - 1;0表示监听元素有部分进入了可视区
        };

        const observer = new IntersectionObserver((entries) => {
            const elem = entries[0];
            const windowWidth = document.documentElement.clientWidth || window.innerWidth;
            const isPcWidth = windowWidth >= 1024;
            if (elem.isIntersecting && elem.boundingClientRect.top > 0) {
                //console.log("enter", "从底部进入");
                if (pc && !pcA && (isPcWidth || (!isPcWidth && !h5))) {
                    setPcA(pc);
                }
                if (h5 && !h5A && !isPcWidth) {
                    setH5A(h5);
                }
            } else if (elem.isIntersecting && elem.boundingClientRect.top < 0) {
                //console.log("enter", "从顶部进入");
                if (pc && !pcA && (isPcWidth || (!isPcWidth && !h5))) {
                    setPcA(pc);
                }
                if (h5 && !h5A && !isPcWidth) {
                    setH5A(h5);
                }
            } else if (!elem.isIntersecting && elem.boundingClientRect.top > 0) {
                //console.log("leave", "从底部离开");
            } else {
                //console.log("leave", "从顶部离开");
            }
        }, options);

        observer.observe(refVideoBox.current);
    }, []);
    useEffect(() => {
        const matchMedia = window.matchMedia('(max-width: 1023px)');
        function handleMediaChange(event: any) {
            if (event.matches) {
                //小于等于1023
                if (h5 && !h5A) {
                    setH5A(h5);
                } else if (!h5 && !pcA) {
                    // 如果没有设置手机端
                    setPcA(pc);
                }
            } else {
                // 大于1023
                //if (pc && !pcA && (isPcWidth || (!isPcWidth && !h5))) {
                if (pc && !pcA) {
                    setPcA(pc);
                }
            }
        }

        matchMedia.addEventListener('change', handleMediaChange);
    }, []);
    return (
        <div className={compCls} ref={refVideoBox}>
            {pcA && (
                <video ref={videoRef1} playsInline muted autoPlay={autoPlay} loop={loop} poster={poster} className={twoSrc ? `${prefixCls}-pc` : ''}>
                    <source src={pcA} />
                </video>
            )}
            {h5A && (
                <video ref={videoRef2} playsInline muted autoPlay={autoPlay} loop={loop} poster={poster} className={twoSrc ? `${prefixCls}-h5` : ''}>
                    <source src={h5A} />
                </video>
            )}
            {showPlayIcon && !isPlay && (
                <svg
                    className={`${prefixCls}-icon`}
                    onClick={playHandle}
                    xmlns="http://www.w3.org/2000/svg"
                    width="100"
                    height="100"
                    viewBox="0 0 100 100"
                    fill="none"
                >
                    <path
                        d="M94.1667 49.8611C94.1667 74.3304 74.3304 94.1667 49.8611 94.1667C25.3918 94.1667 5.55557 74.3304 5.55557 49.8611C5.55557 25.3918 25.3918 5.55557 49.8611 5.55557C74.3304 5.55557 94.1667 25.3918 94.1667 49.8611Z"
                        stroke="white"
                        strokeWidth="2.22222"
                    />
                    <path
                        d="M37.7783 67.9227V34.0886C37.7783 32.3267 39.7308 31.2655 41.2093 32.2239L66.1396 48.3835C67.4577 49.2378 67.4975 51.1526 66.2162 52.0611L41.2858 69.7356C39.8142 70.7789 37.7783 69.7266 37.7783 67.9227Z"
                        fill="white"
                    />
                </svg>
            )}
        </div>
    );
}
